import { useState, useRef, useEffect } from "react";
import { useAppStore } from "../stores/useAppStore";
import IconArrow from "./IconArrow";
import Button from "./Button";
import { motion } from "motion/react";
import AnalyticsWrapper from "./AnalyticsWrapper";
import CardCarousel from "./CardCarousel";

export default function Carousel({ items, initialSlide = 0, itemWidth, itemHeight, renderItem, SlideComponent, section, prevButtonText, nextButtonText, loop = true }) {
  const { cardTouchPos, menuItems } = useAppStore();

  const [currentIndex, setCurrentIndex] = useState(initialSlide);
  const [dragging, setDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const containerRef = useRef(null);

  const [actualItems, setActualItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    // duplicamos el array de items si hay menos de 5 slides porque da problemas de animación en el wrap-around cuano hay 4 slides por ejemplo
    let array = items.length < 5 && loop 
    ? [...items, ...items] 
    : [...items];
    setActualItems(array);
    setTotalItems(array.length);
  }, [items]);

  useEffect(() => {
    setCurrentIndex(initialSlide);
  }, [initialSlide]);

  useEffect(() => {
    if (actualItems.length === 0) return;
    const realIndex = currentIndex % totalItems;
    const currentItem = actualItems[realIndex];
    AnalyticsWrapper.trackPageView(`/${section}/${currentItem.id}`);
  }, [currentIndex, actualItems]);

  const handleStart = (x) => {
    setDragging(true);
    setStartX(x);
    setDragOffset(0);
  };

  const handleMove = (x) => {
    if (!dragging) return;
    const delta = x - startX;
    setDragOffset(delta);
  };

  const handleEnd = () => {
    if (!dragging) return;
    setDragging(false);

    if (dragOffset > 100) {
      prevSlide();
    } else if (dragOffset < -100) {
      nextSlide();
    }

    setDragOffset(0);
  };

  // Eventos de ratón
  const handleMouseDown = (e) => handleStart(e.clientX);
  const handleMouseMove = (e) => handleMove(e.clientX);
  const handleMouseUp = () => handleEnd();
  const handleMouseLeave = () => setDragging(false);

  // Eventos táctiles
  const handleTouchStart = (e) => handleStart(e.touches[0].clientX);
  const handleTouchMove = (e) => handleMove(e.touches[0].clientX);
  const handleTouchEnd = (e) => handleEnd();

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => {
      if (loop) {
        return (prevIndex + 1) % totalItems;
      } else {
        return prevIndex < totalItems - 1 ? prevIndex + 1 : prevIndex;
      }
    });
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => {
      if (loop) {
        return prevIndex === 0 ? totalItems - 1 : prevIndex - 1;
      } else {
        return prevIndex > 0 ? prevIndex - 1 : prevIndex;
      }
    });
  };

  const getSlideStyles = (index) => {
    const offset = index - currentIndex;

    const normalizedOffset =
      offset < -Math.floor(totalItems / 2)
        ? offset + totalItems
        : offset > Math.floor(totalItems / 2)
        ? offset - totalItems
        : offset;

    const distance =
      normalizedOffset * 100 + (dragOffset / window.innerWidth) * 100;

    const proximity = Math.max(0, 1 - Math.abs(distance / 300));
    const scale = 0.6 + proximity * 0.4;
    const zIndex = Math.round(proximity * 10);
    const opacity = 0.5 + proximity * 0.5;
    const blurAmount = 14 * (1 - proximity);

    const transform = `translate3d(${distance}%, 0, 0) scale(${scale})`;

    return {
      transform,
      zIndex,
      opacity,
      filter: `blur(${blurAmount}px)`,
      transition:
        dragging || Math.abs(normalizedOffset) > 1 ? "none" : "transform 0.5s ease",
    };
  };

  const prevIndex = currentIndex === 0 ? totalItems - 1 : currentIndex - 1;
  const nextIndex = (currentIndex + 1) % totalItems;

  const variants = {
    closed: {
        backgroundColor: '#1a2951',
        clipPath: `circle(0 at ${cardTouchPos.x}px ${cardTouchPos.y}px)`,
        transition: {
            duration: 0.5,
            ease: "easeInOut",
            delay: 0.1
        },
    },
    open: {
        backgroundColor: '#061933',
        clipPath: `circle(140% at ${cardTouchPos.x}px ${cardTouchPos.y}px)`,
        transition: {
            duration: 0.7,
            ease: "easeInOut",
            delay: 0.2
        },
    },
};

  return (
    <motion.div
      className="fixed inset-0 z-20 transform-gpu will-change-clip-path"
      variants={variants}
      initial="closed"
      animate="open"
      exit="closed"
    >
      <div
        className="w-full h-full flex items-center justify-center"
        variants={variants}
        initial="closed"
        animate="open"
        exit="closed"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        ref={containerRef}
      >
        <div className="relative w-full h-full flex overflow-hidden items-center justify-center">
          {actualItems.map((card, index) => {
            return (
            <div
                key={`${card.id}_carousel_item_${index}`}
                className="absolute rounded-lg flex
                mt-111u
                items-center justify-center text-xl transform-gpu"
                style={{
                  ...getSlideStyles(index),
                  ...(itemHeight ? { minHeight: itemHeight } : {}),
                  ...(itemWidth ? { width: itemWidth } : {width: '168rem'}),
                }}
              >
                {renderItem
                  ? renderItem(card, index)
                  : SlideComponent
                  ? <SlideComponent item={card} index={index} className="h-full" style={itemHeight ? { minHeight: itemHeight } : {}} />
                  : <CardCarousel item={card} />
                }
              </div>
            );
          })}
        </div>
      </div>

      {/* Prev button - solo si loop o no estamos en el primer slide */}
      {(loop || currentIndex > 0) && (
        <Button
          onClick={prevSlide}
          className="click-effect absolute py-[2.75rem] pl-[2.875rem] pr-[3.125rem] right-[calc(50vw+2.8125rem)] bottom-157u z-50 w-[35.75rem] justify-between"
        >
          <IconArrow className="w-[1.125rem] h-auto transform -scale-x-100" />
          <div>{prevButtonText ?? actualItems[prevIndex]?.name}</div>
        </Button>
      )}
      {/* Next button - solo si loop o no estamos en el último slide */}
      {(loop || currentIndex < totalItems - 1) && (
        <Button
          onClick={nextSlide}
          className="click-effect absolute py-[2.75rem] pl-[2.875rem] pr-[3.125rem] left-[calc(50vw+2.8125rem)] bottom-157u z-50 w-[35.75rem] justify-between"
        >
          <div>{nextButtonText ?? actualItems[nextIndex]?.name}</div>
          <IconArrow className="w-[1.125rem] h-auto" />
        </Button>
      )}

    </motion.div>
  );
};